import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { logService } from '../../services/logService';
import { RootState } from '../../store';
import type { Log, CreateLogInput, LogStatus } from '../../types/log';

interface LogFormProps {
  log: Log | null;
  onClose: () => void;
  onSuccess: () => void;
}

const LogForm: React.FC<LogFormProps> = ({ log, onClose, onSuccess }) => {
  const { subjects } = useSelector((state: RootState) => state.logManager);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState<CreateLogInput>({
    title: log?.title || '',
    description: log?.description || '',
    category: log?.category || 'general',
    priority: log?.priority ?? 1,
    status: log?.status || 'active',
    startTime: log?.startTime,
    endTime: log?.endTime,
    subjectIds: [],
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.title.trim()) return;

    setIsSubmitting(true);
    try {
      if (log) {
        await logService.updateLog(log.id, formData);
      } else {
        await logService.createLog(formData);
      }
      onSuccess();
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (field: keyof CreateLogInput, value: unknown) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleDateChange = (field: 'startTime' | 'endTime', dateStr: string, timeStr: string) => {
    if (!dateStr) {
      handleChange(field, undefined);
      return;
    }
    const dateTime = new Date(`${dateStr}T${timeStr || '00:00'}:00`).getTime();
    handleChange(field, dateTime);
  };

  const formatTimestampToDate = (timestamp: number | undefined): string => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    return date.toISOString().split('T')[0];
  };

  const formatTimestampToTime = (timestamp: number | undefined): string => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    return `${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`;
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />

      <div className="relative w-full max-w-md mx-4 bg-white dark:bg-claude-darkSurface rounded-xl shadow-xl overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 border-b dark:border-claude-darkBorder border-claude-border">
          <h2 className="text-lg font-semibold dark:text-claude-darkText text-claude-text">
            {log ? 'Edit Log' : 'New Log'}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 rounded-lg hover:dark:bg-claude-darkSurfaceHover hover:bg-claude-surfaceHover transition-colors"
          >
            <svg
              className="h-5 w-5 dark:text-claude-darkTextSecondary text-claude-textSecondary"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium dark:text-claude-darkText text-claude-text mb-1">
              Title <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => handleChange('title', e.target.value)}
              placeholder="Enter log title"
              required
              className="w-full px-3 py-2 rounded-lg border dark:border-claude-darkBorder border-claude-border dark:bg-claude-darkSurfaceMuted bg-claude-surfaceMuted dark:text-claude-darkText text-claude-text placeholder:dark:text-claude-darkTextSecondary placeholder:text-claude-textSecondary focus:outline-none focus:ring-2 focus:ring-claude-accent"
            />
          </div>

          <div>
            <label className="block text-sm font-medium dark:text-claude-darkText text-claude-text mb-1">
              Description
            </label>
            <textarea
              value={formData.description || ''}
              onChange={(e) => handleChange('description', e.target.value)}
              placeholder="Optional description"
              rows={3}
              className="w-full px-3 py-2 rounded-lg border dark:border-claude-darkBorder border-claude-border dark:bg-claude-darkSurfaceMuted bg-claude-surfaceMuted dark:text-claude-darkText text-claude-text placeholder:dark:text-claude-darkTextSecondary placeholder:text-claude-textSecondary focus:outline-none focus:ring-2 focus:ring-claude-accent resize-none"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium dark:text-claude-darkText text-claude-text mb-1">
                Category
              </label>
              <select
                value={formData.category || 'general'}
                onChange={(e) => handleChange('category', e.target.value)}
                className="w-full px-3 py-2 rounded-lg border dark:border-claude-darkBorder border-claude-border dark:bg-claude-darkSurfaceMuted bg-claude-surfaceMuted dark:text-claude-darkText text-claude-text focus:outline-none focus:ring-2 focus:ring-claude-accent"
              >
                <option value="general">General</option>
                <option value="meeting">Meeting</option>
                <option value="deadline">Deadline</option>
                <option value="todo">Todo</option>
                <option value="reminder">Reminder</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium dark:text-claude-darkText text-claude-text mb-1">
                Priority
              </label>
              <select
                value={formData.priority ?? 1}
                onChange={(e) => handleChange('priority', parseInt(e.target.value))}
                className="w-full px-3 py-2 rounded-lg border dark:border-claude-darkBorder border-claude-border dark:bg-claude-darkSurfaceMuted bg-claude-surfaceMuted dark:text-claude-darkText text-claude-text focus:outline-none focus:ring-2 focus:ring-claude-accent"
              >
                <option value={0}>Low</option>
                <option value={1}>Medium</option>
                <option value={2}>High</option>
                <option value={3}>Urgent</option>
              </select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium dark:text-claude-darkText text-claude-text mb-1">
                Start Date
              </label>
              <input
                type="date"
                value={formatTimestampToDate(formData.startTime)}
                onChange={(e) => handleDateChange('startTime', e.target.value, formatTimestampToTime(formData.startTime))}
                className="w-full px-3 py-2 rounded-lg border dark:border-claude-darkBorder border-claude-border dark:bg-claude-darkSurfaceMuted bg-claude-surfaceMuted dark:text-claude-darkText text-claude-text focus:outline-none focus:ring-2 focus:ring-claude-accent"
              />
            </div>

            <div>
              <label className="block text-sm font-medium dark:text-claude-darkText text-claude-text mb-1">
                Start Time
              </label>
              <input
                type="time"
                value={formatTimestampToTime(formData.startTime)}
                onChange={(e) => handleDateChange('startTime', formatTimestampToDate(formData.startTime) || '', e.target.value)}
                className="w-full px-3 py-2 rounded-lg border dark:border-claude-darkBorder border-claude-border dark:bg-claude-darkSurfaceMuted bg-claude-surfaceMuted dark:text-claude-darkText text-claude-text focus:outline-none focus:ring-2 focus:ring-claude-accent"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium dark:text-claude-darkText text-claude-text mb-1">
                End Date
              </label>
              <input
                type="date"
                value={formatTimestampToDate(formData.endTime)}
                onChange={(e) => handleDateChange('endTime', e.target.value, formatTimestampToTime(formData.endTime))}
                className="w-full px-3 py-2 rounded-lg border dark:border-claude-darkBorder border-claude-border dark:bg-claude-darkSurfaceMuted bg-claude-surfaceMuted dark:text-claude-darkText text-claude-text focus:outline-none focus:ring-2 focus:ring-claude-accent"
              />
            </div>

            <div>
              <label className="block text-sm font-medium dark:text-claude-darkText text-claude-text mb-1">
                End Time
              </label>
              <input
                type="time"
                value={formatTimestampToTime(formData.endTime)}
                onChange={(e) => handleDateChange('endTime', formatTimestampToDate(formData.endTime) || '', e.target.value)}
                className="w-full px-3 py-2 rounded-lg border dark:border-claude-darkBorder border-claude-border dark:bg-claude-darkSurfaceMuted bg-claude-surfaceMuted dark:text-claude-darkText text-claude-text focus:outline-none focus:ring-2 focus:ring-claude-accent"
              />
            </div>
          </div>

          {log && (
            <div>
              <label className="block text-sm font-medium dark:text-claude-darkText text-claude-text mb-1">
                Status
              </label>
              <select
                value={formData.status || 'active'}
                onChange={(e) => handleChange('status', e.target.value as LogStatus)}
                className="w-full px-3 py-2 rounded-lg border dark:border-claude-darkBorder border-claude-border dark:bg-claude-darkSurfaceMuted bg-claude-surfaceMuted dark:text-claude-darkText text-claude-text focus:outline-none focus:ring-2 focus:ring-claude-accent"
              >
                <option value="active">Active</option>
                <option value="completed">Completed</option>
                <option value="archived">Archived</option>
                <option value="cancelled">Cancelled</option>
              </select>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium dark:text-claude-darkText text-claude-text mb-1">
              Subjects
            </label>
            <select
              multiple
              value={formData.subjectIds || []}
              onChange={(e) => {
                const selected = Array.from(e.target.selectedOptions).map((opt) => opt.value);
                handleChange('subjectIds', selected);
              }}
              className="w-full px-3 py-2 rounded-lg border dark:border-claude-darkBorder border-claude-border dark:bg-claude-darkSurfaceMuted bg-claude-surfaceMuted dark:text-claude-darkText text-claude-text focus:outline-none focus:ring-2 focus:ring-claude-accent min-h-[100px]"
            >
              {subjects.map((subject) => (
                <option key={subject.id} value={subject.id}>
                  {subject.name} ({subject.type})
                </option>
              ))}
            </select>
          </div>

          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 text-sm font-medium rounded-lg border dark:border-claude-darkBorder border-claude-border dark:text-claude-darkText text-claude-text hover:dark:bg-claude-darkSurfaceHover hover:bg-claude-surfaceHover transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting || !formData.title.trim()}
              className="flex-1 px-4 py-2 text-sm font-medium rounded-lg bg-claude-accent text-white hover:bg-claude-accent/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? 'Saving...' : log ? 'Save Changes' : 'Create Log'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default LogForm;